import EmptyState from '@molecules/EmptyState'
import AppErrorsAlert from '@components/molecules/Alerts/AppErrorsAlert'
import Button from '@atoms/Button'
import { TooltipProvider } from '@components/atoms/Tooltip'

import { scopedTranslation } from '@utils/I18n'

import { RosterDayGroup } from './RosterDayGroup'
import { groupShiftsByDay } from './rosterCoverage'
import type { LayoutMode } from './layoutMode'

import { AddShiftButton } from '../author_shifts/AddShiftButton'
import { useShiftAuthoring } from '../author_shifts/ShiftAuthoringContext'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('roster_board')

type RosterBoardProps = {
  shifts: ShiftWithSlots[]
  totalShiftCount: number
  filtersActive: boolean
  layout: LayoutMode
  onClearFilters: () => void
}

export function RosterBoard({ shifts, totalShiftCount, filtersActive, layout, onClearFilters }: RosterBoardProps) {
  const { dates, timeZone, onChange } = useShiftAuthoring()

  if (totalShiftCount === 0) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-6">
        <AppErrorsAlert errorsPath="rosterAssignment" />
        <EmptyState iconType="calendar" headline={t('empty_headline')} subtext={t('empty_subtext')}>
          <AddShiftButton />
        </EmptyState>
      </div>
    )
  }

  if (shifts.length === 0) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-6">
        <AppErrorsAlert errorsPath="rosterAssignment" />
        <div role="status">
          <EmptyState iconType="funnel" headline={t('filters.empty_headline')} subtext={t('filters.empty_subtext')}>
            <Button rank="secondary" leadingIcon="refresh" onClick={onClearFilters}>
              {t('filters.chips.clear')}
            </Button>
          </EmptyState>
        </div>
      </div>
    )
  }

  const groups = groupShiftsByDay(shifts, dates, timeZone).filter((group) => !filtersActive || group.shifts.length > 0)

  return (
    <TooltipProvider>
      <div className="tw:flex tw:flex-col tw:gap-6" data-testid="roster-board">
        <AppErrorsAlert errorsPath="rosterAssignment" />
        {groups.map((group) => (
          <RosterDayGroup
            key={group.date}
            date={group.date}
            shifts={group.shifts}
            layout={layout}
            onChange={onChange}
          />
        ))}
      </div>
    </TooltipProvider>
  )
}
